// Vocabulário e formatação compartilhados pelas telas do módulo de
// monitoramento (M6b): fluxo do cartão, métricas do quadro, métricas por
// pessoa. O backend (flowMetrics.js) é a fonte da verdade das fases —
// aqui mora só apresentação (rótulo em PT-BR, cor do tema, duração legível).

/** Espelha PHASES de backend/src/services/flowMetrics.js, na mesma ordem. */
export const PHASES = ['unstarted', 'started', 'waiting', 'completed', 'canceled'];
export const UNPHASED = 'unphased';

export const PHASE_LABELS = {
  unstarted: 'Não iniciado',
  started: 'Em andamento',
  waiting: 'Aguardando',
  completed: 'Concluído',
  canceled: 'Cancelado',
  unphased: 'Sem fase',
};

// Classes Tailwind — cada uma resolve pela var(--x) do tema (globals.css),
// então dark/light e a customização de cor da org (accent) continuam
// funcionando sem nenhum hex fixo aqui.
const PHASE_COLOR = {
  unstarted: { bg: 'bg-text-secondary/50', fill: 'fill-text-secondary/50', text: 'text-text-secondary' },
  started: { bg: 'bg-accent', fill: 'fill-accent', text: 'text-accent' },
  waiting: { bg: 'bg-warning', fill: 'fill-warning', text: 'text-warning' },
  completed: { bg: 'bg-success', fill: 'fill-success', text: 'text-success' },
  canceled: { bg: 'bg-danger', fill: 'fill-danger', text: 'text-danger' },
  unphased: { bg: 'bg-text-secondary/25', fill: 'fill-text-secondary/25', text: 'text-text-secondary/70' },
};

export function phaseLabel(phase) {
  return PHASE_LABELS[phase] || phase || '—';
}

export function phaseColor(phase) {
  return PHASE_COLOR[phase] || PHASE_COLOR.unphased;
}

// `coverage.reason` de GET /cards/:id/flow (metrics.routes.js, M6a) — frase
// curta e honesta, nunca um número inventado no lugar de "não sabemos".
const START_UNKNOWN_REASON_LABELS = {
  no_workflow_column: 'Este quadro não tem uma coluna de status definida como fluxo de trabalho.',
  no_transitions: 'Este cartão não tem nenhuma mudança de status registrada.',
  unphased_labels: 'As etiquetas de status usadas aqui ainda não foram classificadas em uma fase do fluxo.',
  no_started_transition: 'O histórico é anterior ao início do rastreamento de fluxo — não é possível saber quando entrou em andamento.',
};

export function startUnknownReason(reason) {
  return START_UNKNOWN_REASON_LABELS[reason] || 'Início desconhecido.';
}

/** Milissegundos -> duração legível e compacta ("2d 4h", "35min", "<1min"). */
export function formatDuration(ms) {
  if (ms == null || Number.isNaN(ms)) return '—';
  const abs = Math.max(0, ms);
  const min = Math.round(abs / 60000);
  if (min < 1) return '<1min';
  if (min < 60) return `${min}min`;
  const hours = Math.floor(min / 60);
  const remMin = min % 60;
  if (hours < 24) return remMin ? `${hours}h ${remMin}min` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  if (days < 30) return remHours ? `${days}d ${remHours}h` : `${days}d`;
  const months = Math.floor(days / 30);
  const remDays = days % 30;
  return remDays ? `${months}m ${remDays}d` : `${months}m`;
}

/**
 * "YYYY-MM-DD" -> "DD/MM". String, nunca `Date()`: estes rótulos (cfd_days,
 * cfd[].day, throughput[].week_start) já vêm agrupados no fuso da
 * ORGANIZAÇÃO (BOARD_METRICS_SQL, M6a) — reinterpretar com `new Date(...)`
 * aplicaria por cima o fuso do NAVEGADOR, um agrupamento diferente do que o
 * backend fez. Pedido explícito do marco: exibir como vier.
 */
export function formatDayLabel(dayStr) {
  if (!dayStr || typeof dayStr !== 'string') return '';
  const [, m, d] = dayStr.split('-');
  return m && d ? `${d}/${m}` : dayStr;
}

export const formatWeekLabel = formatDayLabel;
